import React from 'react';
import { Activity, Heart, Users, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import ServicePageLayout from './ServicePageLayout';
import { useTranslation } from '../../hooks/useTranslation';

export default function Physiotherapist() {
  const { t } = useTranslation();
  
  return (
    <ServicePageLayout
      title={t('services.physiotherapist.title')}
      subtitle={t('services.physiotherapist.subtitle')}
      icon={Activity}
      heroColor="from-violet-500 to-purple-600"
      enquiryTitle={t('services.physiotherapist.title')}
    >
      {/* Main Description */}
      <Card className="mb-8 shadow-lg" data-testid="service-main-card">
        <CardContent className="p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('services.physiotherapist.mainTitle')}</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            {t('services.physiotherapist.mainDesc1')}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {t('services.physiotherapist.mainDesc2')}
          </p>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-certified">
          <CardContent className="p-6">
            <Users className="w-12 h-12 text-violet-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.physiotherapist.features.certified.title')}</h3>
            <p className="text-gray-700">
              {t('services.physiotherapist.features.certified.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-personalized">
          <CardContent className="p-6">
            <Heart className="w-12 h-12 text-violet-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.physiotherapist.features.personalized.title')}</h3>
            <p className="text-gray-700">
              {t('services.physiotherapist.features.personalized.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-flexible">
          <CardContent className="p-6">
            <Clock className="w-12 h-12 text-violet-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.physiotherapist.features.flexible.title')}</h3>
            <p className="text-gray-700">
              {t('services.physiotherapist.features.flexible.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-equipment">
          <CardContent className="p-6">
            <Activity className="w-12 h-12 text-violet-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.physiotherapist.features.equipment.title')}</h3>
            <p className="text-gray-700">
              {t('services.physiotherapist.features.equipment.desc')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Treatments */}
      <Card className="mb-8 shadow-lg" data-testid="treatments-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.physiotherapist.treatmentsTitle')}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {t('services.physiotherapist.treatmentsList').map((treatment) => (
              <div key={treatment} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-violet-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{treatment}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Session Types */}
      <Card className="mb-8 shadow-lg" data-testid="sessions-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.physiotherapist.sessionsTitle')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-violet-50 rounded-xl p-6 border border-violet-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.physiotherapist.sessions.single.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.physiotherapist.sessions.single.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.physiotherapist.sessions.single.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-violet-50 rounded-xl p-6 border border-violet-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.physiotherapist.sessions.package.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.physiotherapist.sessions.package.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.physiotherapist.sessions.package.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-violet-50 rounded-xl p-6 border border-violet-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.physiotherapist.sessions.intensive.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.physiotherapist.sessions.intensive.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.physiotherapist.sessions.intensive.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </ServicePageLayout>
  );
}
